import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Share2, Linkedin, Twitter, Copy, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { MaskedLines, Reveal, EASE } from "@/components/Motion";
import { CompanyAvatar } from "@/components/CompanyAvatar";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const announce = (year, c) => `${c.winner.title} wins ${c.name} at the Balqis Partners Awards ${year}.`;

function share(year, c, net) {
  const url = `${window.location.origin}/awards/${year}/ceremony#${slug(c.name)}`;
  const text = announce(year, c);
  if (net === "linkedin") window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, "_blank", "noopener");
  else if (net === "x") window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, "_blank", "noopener");
  else if (net === "native" && navigator.share) navigator.share({ title: "Balqis Partners Awards", text, url }).catch(() => {});
  else { navigator.clipboard?.writeText(`${text} ${url}`); toast.success("Announcement copied"); }
}

const Partners = ({ partners = [] }) => partners.length > 0 && (
  <div className="flex items-center justify-center gap-3">
    {partners.map((p, i) => (
      <span key={p.id} className="flex items-center gap-3">
        {i > 0 && <span className="font-display text-2xl text-[#E0218A]">×</span>}
        <Link to={`/companies/${p.id}`} className="flex flex-col items-center gap-2" data-testid={`ceremony-partner-${p.id}`}>
          <CompanyAvatar c={p} size="h-14 w-14" text="text-xl" />
          <span className="text-xs text-slate-300">{p.name}</span>
        </Link>
      </span>
    ))}
  </div>
);

function WinnerCard({ year, c, i }) {
  const w = c.winner;
  return (
    <Reveal delay={(i % 3) * 0.06}>
      <article id={slug(c.name)} className="relative h-full overflow-hidden rounded-3xl border border-[#E0218A]/30 bg-[#FFFFFF] p-8 text-center scroll-mt-28" data-testid={`ceremony-winner-${i}`}>
        <div className="pointer-events-none absolute -top-24 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full bg-[#E0218A]/20 blur-3xl" />
        <motion.div initial={{ rotate: -12, scale: 0.7, opacity: 0 }} whileInView={{ rotate: 0, scale: 1, opacity: 1 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.9, ease: EASE }}
          className="relative mx-auto grid h-20 w-20 place-items-center rounded-full bg-gradient-to-b from-[#8E2BC4] to-[#B5157A] text-white shadow-[0_0_60px_rgba(224,33,138,0.35)]">
          <Trophy className="h-9 w-9" />
        </motion.div>
        <p className="relative mt-6 text-[11px] uppercase tracking-[0.28em] text-[#8E2BC4]">{c.name}</p>
        <h3 className="relative mt-3 font-display text-3xl text-[#2A1240]" data-testid={`ceremony-winner-title-${i}`}>{w.title}</h3>
        {w.description && <p className="relative mt-3 text-sm text-slate-300">{w.description}</p>}
        <div className="relative mt-6"><Partners partners={w.partners} /></div>
        <p className="relative mt-5 font-mono text-xs text-slate-400">{w.votes} member vote{w.votes === 1 ? "" : "s"} · nominated by {w.nominated_by_company_name}</p>
        <div className="relative mt-6 flex flex-wrap justify-center gap-2">
          <button onClick={() => share(year, c, "linkedin")} className="grid h-9 w-9 place-items-center rounded-full border border-[#2A1240]/15 text-slate-200 hover:border-[#E0218A]" aria-label="Share on LinkedIn" data-testid={`ceremony-share-linkedin-${i}`}><Linkedin className="h-4 w-4" /></button>
          <button onClick={() => share(year, c, "x")} className="grid h-9 w-9 place-items-center rounded-full border border-[#2A1240]/15 text-slate-200 hover:border-[#E0218A]" aria-label="Share on X" data-testid={`ceremony-share-x-${i}`}><Twitter className="h-4 w-4" /></button>
          {typeof navigator !== "undefined" && navigator.share && <button onClick={() => share(year, c, "native")} className="grid h-9 w-9 place-items-center rounded-full border border-[#2A1240]/15 text-slate-200 hover:border-[#E0218A]" aria-label="Share" data-testid={`ceremony-share-native-${i}`}><Share2 className="h-4 w-4" /></button>}
          <button onClick={() => share(year, c, "copy")} className="inline-flex items-center gap-1.5 rounded-full border border-[#2A1240]/15 px-3 text-xs text-slate-200 hover:border-[#E0218A]" data-testid={`ceremony-share-copy-${i}`}><Copy className="h-3.5 w-3.5" />Copy announcement</button>
        </div>
        {c.nominees.filter((n) => !n.winner).length > 0 && (
          <div className="relative mt-7 border-t border-[#2A1240]/10 pt-5 text-left">
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Finalists</p>
            {c.nominees.filter((n) => !n.winner).slice(0, 3).map((n) => <p key={n.id} className="mt-2 flex justify-between text-sm text-slate-300"><span className="truncate">{n.title}</span><span className="font-mono text-slate-400">{n.votes}</span></p>)}
          </div>
        )}
      </article>
    </Reveal>
  );
}

function Pending({ c, i }) {
  const lead = c.nominees[0];
  return (
    <Reveal delay={(i % 3) * 0.06}>
      <article className="h-full rounded-3xl border border-[#2A1240]/10 bg-[#FFFFFF] p-8 text-center" data-testid={`ceremony-pending-${i}`}>
        <div className="mx-auto grid h-20 w-20 place-items-center rounded-full border border-dashed border-[#E0218A]/50 text-[#E0218A]/70"><Trophy className="h-9 w-9" /></div>
        <h3 className="mt-6 font-display text-2xl text-[#2A1240]">{c.name}</h3>
        {lead && <p className="mt-3 text-sm text-slate-300">{`${c.nominees.length} nominee${c.nominees.length === 1 ? "" : "s"} · leading: ${lead.title} (${lead.votes} votes)`}</p>}
      </article>
    </Reveal>
  );
}

export default function AwardsCeremony() {
  const { year } = useParams();
  useSeo(`Balqis Partners Awards ${year} Ceremony`, `Winners, nominees and highlights from the ${year} Balqis Partners Awards ceremony.`, `/awards/${year}/ceremony`);
  const [data, setData] = useState(null);
  const [years, setYears] = useState([]);
  useEffect(() => {
    api.get(`/awards?year=${Number(year) || 0}`).then((r) => setData(r.data)).catch(() => setData({ categories: [] }));
    api.get("/awards/years").then((r) => setYears(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [year]);
  useEffect(() => {
    if (!data || !window.location.hash) return undefined;
    const t = setTimeout(() => document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 500);
    return () => clearTimeout(t);
  }, [data]);
  const cats = Array.isArray(data?.categories) ? data.categories : [];
  const winners = cats.filter((c) => c.winner).length;
  return (
    <div className="min-h-screen bg-[#FFF7FB]" data-testid="ceremony-page">
      <Nav />
      <section className="relative overflow-hidden pt-32 pb-20">
        <div className="relative mx-auto max-w-5xl px-5 sm:px-8 text-center">
          <Link to="/awards" className="inline-flex items-center gap-2 text-xs text-slate-400 hover:text-[#2A1240]" data-testid="ceremony-back"><ArrowLeft className="h-3.5 w-3.5" />All categories & voting</Link>
          <p className="kicker mt-8">Balqis Partners Awards · {year}</p>
          <MaskedLines as="h1" className="mt-5 font-display text-4xl sm:text-6xl lg:text-7xl text-[#2A1240] tracking-tight" lines={["The night’s", <em key="w" className="italic gold-text">winners.</em>]} />
          <p className="mt-6 text-base md:text-lg text-slate-300" data-testid="ceremony-summary">{winners ? `${winners} of ${cats.length} categories crowned.` : "Winners are being decided — check back after the jury announces."}</p>
          {years.length > 1 && (
            <div className="mt-6 flex justify-center gap-2">{years.map((y) => <Link key={y.year} to={`/awards/${y.year}/ceremony`} className={`rounded-full px-3 py-1 text-xs ${String(y.year) === String(year) ? "bg-[#E0218A] text-white font-bold" : "border border-[#2A1240]/15 text-slate-300"}`} data-testid={`ceremony-year-${y.year}`}>{y.year}</Link>)}</div>
          )}
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {cats.map((c, i) => (c.winner ? <WinnerCard key={c.name} year={year} c={c} i={i} /> : <Pending key={c.name} c={c} i={i} />))}
      </section>
      <Footer />
    </div>
  );
}
